// frontend/src/components/ObjectiveBoard/index.js

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  MapPin,
  Search,
  LayoutGrid,
  UserRound,
  X
} from "lucide-react"; // <-- Import Search icon
import { apiService } from "../../services/api";
import { useAuth } from "../Auth";
import { useNavigate, useLocation } from "react-router-dom";
import { useNexusSync } from "../../context/NexusSyncContext";
import { getProjectPriorityInfo, getProjectPriorityValue } from "../../utils/projectPriority";

// Composants
import AddObjectiveForm from "./AddObjectiveForm";
import KanbanColumn from "./KanbanColumn";
import CommentPopup from "./CommentPopup";

const STATUS_KEYS = ["not_started", "in_progress", "blocked", "done"];
const COLUMN_BATCH_SIZE = 20;
const DONE_OBJECTIVES_PAGE_SIZE = 10;
const NON_DONE_STATUSES = ["not_started", "in_progress", "blocked"];
const CACHE_PREFIX = "objective_board_cache_v2";
const MY_OBJECTIVES_SCOPE_STORAGE_KEY = "objectiveBoard_myObjectives_sprintFilter_v2";
const CACHE_TTL_MS = {
  projects: 5 * 60 * 1000,
  employees: 5 * 60 * 1000,
  slots: 60 * 1000,
  sprints: 60 * 1000,
};

const readCache = (key, ttlMs) => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(`${CACHE_PREFIX}:${key}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const timestamp = Number(parsed.timestamp || 0);
    if (!Number.isFinite(timestamp) || Date.now() - timestamp > ttlMs) {
      window.localStorage.removeItem(`${CACHE_PREFIX}:${key}`);
      return null;
    }
    return parsed.data ?? null;
  } catch {
    return null;
  }
};

const writeCache = (key, data) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      `${CACHE_PREFIX}:${key}`,
      JSON.stringify({ timestamp: Date.now(), data })
    );
  } catch {
    // ignore quota and serialization errors
  }
};

const clearCacheByPrefix = (keyPrefix) => {
  if (typeof window === "undefined") return;
  try {
    const fullPrefix = `${CACHE_PREFIX}:${keyPrefix}`;
    for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
      const key = window.localStorage.key(index);
      if (key && key.startsWith(fullPrefix)) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    // ignore localStorage access errors
  }
};

const isSlotCurrent = (slot) => {
  if (!slot?.start_date || !slot?.end_date) {
    return false;
  }
  const now = new Date();
  const start = new Date(slot.start_date);
  const end = new Date(slot.end_date);
  return now >= start && now <= end;
};

const createInitialVisibleCounts = () =>
  STATUS_KEYS.reduce((acc, key) => {
    acc[key] = COLUMN_BATCH_SIZE;
    return acc;
  }, {});

const ObjectiveBoard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialRenderRef = useRef(true);
  const previousSearchActiveRef = useRef(false);
  const { lastMutation } = useNexusSync();

  // Récupérer les paramètres d'URL
  const queryParams = new URLSearchParams(location.search);
  const projectParam = queryParams.get('project');
  const ownerParam = queryParams.get('owner');
  const sprintParam = queryParams.get('sprint');
  const viewModeParam = queryParams.get('view');

  const [viewMode, setViewMode] = useState(() => {
    if (viewModeParam === 'myObjectives') return 'myObjectives';
    return localStorage.getItem("objectiveBoard_viewMode") || "project";
  });

  // Conversion des paramètres en tableaux pour les sélections multiples
  const getProjectsFromParam = () => {
    if (!projectParam) {
      const storedProject = localStorage.getItem("objectiveBoard_selectedProject");
      if (storedProject === "all" || !storedProject) return ["all"];
      // Traiter comme une liste séparée par des virgules, comme pour les paramètres d'URL
      return storedProject.includes(',') ? storedProject.split(',').filter(Boolean) : [storedProject];
    }
    return projectParam.split(',').filter(Boolean);
  };
  const [selectedProjects, setSelectedProjects] = useState(getProjectsFromParam());
  // Pour la compatibilité avec le reste du code
  const selectedProject = selectedProjects.length > 0 && selectedProjects[0] !== "all" ? selectedProjects[0] : "";

  const getOwnersFromParam = () => {
    if (!ownerParam) {
      const storedOwner = localStorage.getItem("objectiveBoard_filterOwner");
      if (!storedOwner) return ["all"];
      // Traiter comme une liste séparée par des virgules, comme pour les paramètres d'URL
      return storedOwner.includes(',') ? storedOwner.split(',').filter(Boolean) : [storedOwner];
    }
    return ownerParam.split(',').filter(Boolean);
  };
  const [filterOwners, setFilterOwners] = useState(getOwnersFromParam());
  // Pour la compatibilité avec le reste du code
  const filterOwner = filterOwners.includes("all") ? "all" : filterOwners[0] || "all";

  const [projectSprintFilter, setProjectSprintFilter] = useState(() =>
    sprintParam || localStorage.getItem("objectiveBoard_project_sprintFilter") || "all"
  );
  const [myObjectivesSprintFilter, setMyObjectivesSprintFilter] = useState(() =>
    localStorage.getItem(MY_OBJECTIVES_SCOPE_STORAGE_KEY) === "enCours" ? "enCours" : "all"
  );
  const [projects, setProjects] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [visibleObjectivesCount, setVisibleObjectivesCount] = useState(createInitialVisibleCounts);

  const [employees, setEmployees] = useState([]);
  const [employee, setEmployee] = useState(null);
  // Pour les sprints (détaillés pour "par projet" et options fixes pour "mes objectifs")
  const [sprints, setSprints] = useState([]);
  const [sprintOptions, setSprintOptions] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newObjective, setNewObjective] = useState({
    project_id: "",
    name: "",
    description: "",
    sprint_id: null,
    effort: 0,
    owner_id: ""
  });
  // Pour l'édition inline
  const [editing, setEditing] = useState({ objectiveId: null, field: null, value: "" });
  // Pour le drag & drop
  const [draggedObjective, setDraggedObjective] = useState(null);
  const [currentSprintsMapping, setCurrentSprintsMapping] = useState({});
  const [allSprints, setAllSprints] = useState({});
  const [iterationsById] = useState({});
  const sprintsFetchKeyRef = useRef(null);
  const sprintsFetchInFlightRef = useRef(false);
  const [projectParticipants, setProjectParticipants] = useState({});
  const [objectivesRefreshToken, setObjectivesRefreshToken] = useState(0);
  const [sprintsRefreshToken, setSprintsRefreshToken] = useState(0);
  const [doneObjectivesPage, setDoneObjectivesPage] = useState({
    offset: 0,
    hasMore: false,
    loading: false,
  });
  const doneObjectivesScopeRef = useRef({ projectIds: [], ownerId: null });
  const [objectivesLoading, setObjectivesLoading] = useState(false);
  const objectivesLoadIdRef = useRef(0);

  const [newObjectiveAnimating, setNewObjectiveAnimating] = useState(false);
  const [pendingNewObjective, setPendingNewObjective] = useState(null);

  const [predictedTokens, setPredictedTokens] = useState(null);
  const [predictingTokens, setPredictingTokens] = useState(false);
  // --- NOUVEAU: Prédiction IA du titre ---
  const [predictedTitle, setPredictedTitle] = useState(null);
  const [predictingTitle, setPredictingTitle] = useState(false);
  const [museTitleRewrite, setMuseTitleRewrite] = useState(null); // { from, to } : titre reformulé par Muse
  const pendingNameRef = useRef("");
  pendingNameRef.current = pendingNewObjective?.name || "";
  const [titleSuggestionDismissed, setTitleSuggestionDismissed] = useState(false);
  // --- FIN NOUVEAU ---

  // Nouveaux états pour le popup de commentaire
  const [commentObjective, setCommentObjective] = useState(null);
  const [commentText, setCommentText] = useState("");

  const [projectSearch, setProjectSearch] = useState("");
  const filteredProjects = useMemo(() => {
    const search = projectSearch.toLowerCase();
    return projects
      .filter((project) => (project.name || '').toLowerCase().includes(search))
      .sort((a, b) => {
        const priorityDiff = getProjectPriorityValue(a) - getProjectPriorityValue(b);
        if (priorityDiff !== 0) return priorityDiff;
        return String(a.project_number || a.name || '').localeCompare(String(b.project_number || b.name || ''));
      });
  }, [projects, projectSearch]);
  const projectById = useMemo(() => {
    const map = new Map();
    projects.forEach((project) => {
      if (project?.id !== undefined && project?.id !== null) {
        map.set(String(project.id), project);
      }
    });
    return map;
  }, [projects]);

  // Recherche d'owner
  const [ownerSearch, setOwnerSearch] = useState("");
  const filteredEmployees = employees.filter(emp =>
    emp.name.toLowerCase().includes(ownerSearch.toLowerCase())
  );
  const activeEmployees = useMemo(
    () => employees.filter(emp => emp.is_active !== false),
    [employees]
  );

  const [activeFilterDropdown, setActiveFilterDropdown] = useState(null);
  const projectDropdownRef = useRef(null);
  const ownerDropdownRef = useRef(null);
  const slotDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!activeFilterDropdown) return;
      const map = {
        project: projectDropdownRef,
        owner: ownerDropdownRef,
        slot: slotDropdownRef
      };
      const ref = map[activeFilterDropdown];
      if (ref?.current && !ref.current.contains(event.target)) {
        setActiveFilterDropdown(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setActiveFilterDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [activeFilterDropdown]);

  useEffect(() => {
    if (activeFilterDropdown !== "project") {
      setProjectSearch("");
    }
    if (activeFilterDropdown !== "owner") {
      setOwnerSearch("");
    }
  }, [activeFilterDropdown]);

  // Nouvel état pour la sélection multiple de projets
  const [selectedProjectsMap, setSelectedProjectsMap] = useState({});
  // Nouvel état pour la sélection multiple d'owners
  const [selectedOwnersMap, setSelectedOwnersMap] = useState({});

  // Nouveau state pour les slots
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("all");

  // --- NOUVEAU: State for search term ---
  const [searchTerm, setSearchTerm] = useState('');
  const isSearchActive = searchTerm.trim() !== "";
  // ------------------------------------
  const slotSprintIdsMap = useMemo(() => {
    if (!Array.isArray(slots) || !Array.isArray(sprints) || slots.length === 0 || sprints.length === 0) {
      return {};
    }

    const parseDate = (value) => {
      if (!value) return null;
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return null;
      return parsed;
    };

    return slots.reduce((acc, slot) => {
      const slotStart = parseDate(slot.start_date);
      const slotEnd = parseDate(slot.end_date);
      if (!slotStart || !slotEnd) {
        acc[String(slot.id)] = [];
        return acc;
      }

      acc[String(slot.id)] = sprints
        .filter((sprint) => {
          const sprintStart = parseDate(sprint.start_date);
          const sprintEnd = parseDate(sprint.end_date);
          if (!sprintStart || !sprintEnd) return false;
          return sprintStart <= slotEnd && sprintEnd >= slotStart;
        })
        .map((sprint) => String(sprint.id));

      return acc;
    }, {});
  }, [slots, sprints]);

  const buildProjectParticipants = useCallback(
    (projectId, focusEntries) => {
      if (!projectId || !Array.isArray(focusEntries)) return [];
      const token = `@[project:${projectId}|`;
      const byId = new Map();
      focusEntries.forEach((entry) => {
        if (!entry?.focus_text || !entry.focus_text.includes(token)) return;
        const employee = employees.find((emp) => emp.id === entry.employee_id);
        if (employee) {
          byId.set(employee.id, employee);
        }
      });
      return Array.from(byId.values()).sort((a, b) =>
        (a?.name || "").localeCompare(b?.name || ""),
      );
    },
    [employees],
  );

  const filtersSignature = useMemo(
    () =>
      JSON.stringify({
        viewMode,
        selectedProjects,
        filterOwners,
        projectSprintFilter,
        myObjectivesSprintFilter,
        selectedSlot
      }),
    [
      viewMode,
      selectedProjects,
      filterOwners,
      projectSprintFilter,
      myObjectivesSprintFilter,
      selectedSlot
    ]
  );

  useEffect(() => {
    if (!isSearchActive) {
      setVisibleObjectivesCount(createInitialVisibleCounts());
    }
  }, [filtersSignature, isSearchActive]);

  useEffect(() => {
    if (isSearchActive && !previousSearchActiveRef.current) {
      const kanbanAnchor = document.getElementById("objective-board-kanban");
      if (kanbanAnchor) {
        kanbanAnchor.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    previousSearchActiveRef.current = isSearchActive;
  }, [isSearchActive]);

  const handleViewModeChange = useCallback((mode) => {
    // Evite l'effet "ancien onglet visible" pendant le switch
    objectivesLoadIdRef.current += 1;
    setObjectives([]);
    setObjectivesLoading(true);
    setDoneObjectivesPage({ offset: 0, hasMore: false, loading: true });
    setViewMode(mode);
    const params = new URLSearchParams(location.search);
    if (mode === "myObjectives") {
      params.set("view", "myObjectives");
    } else {
      params.delete("view");
    }
    navigate(
      `${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`,
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  // Récupère l'objet projet correspondant au premier projet sélectionné
  const selectedProjectObj = projects.find(
    (p) => p.id?.toString() === selectedProject?.toString()
  );
  const selectedProjectName = selectedProjectObj ? selectedProjectObj.name : "";

  useEffect(() => {
    localStorage.setItem("objectiveBoard_viewMode", viewMode);
  }, [viewMode]);

  // Mettre à jour URL et localStorage uniquement quand les sélections changent
  // et après le premier rendu
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    // Mise à jour de l'URL avec les projets et owners sélectionnés
    const params = new URLSearchParams(location.search);

    // Préserver le paramètre view s'il existe
    const viewParam = params.get('view');

    if (selectedProjects.length > 0 && !selectedProjects.includes("all")) {
      params.set('project', selectedProjects.join(','));
    } else {
      params.delete('project');
    }

    if (filterOwners.length > 0 && !filterOwners.includes("all")) {
      params.set('owner', filterOwners.join(','));
    } else {
      params.delete('owner');
    }

    if (projectSprintFilter !== "all") {
      params.set('sprint', projectSprintFilter);
    } else {
      params.delete('sprint');
    }

    // Remettre le paramètre view s'il existait
    if (viewParam) {
      params.set('view', viewParam);
    }

    const newUrl = `${location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState(null, '', newUrl);

    // Enregistrement dans le localStorage
    if (selectedProjects.length > 0) {
      localStorage.setItem("objectiveBoard_selectedProject", selectedProjects.join(','));
    }

    if (filterOwners.length > 0) {
      localStorage.setItem("objectiveBoard_filterOwner", filterOwners.join(','));
    }
  }, [selectedProjects, filterOwners, projectSprintFilter, location.pathname]);

  useEffect(() => {
    localStorage.setItem("objectiveBoard_project_sprintFilter", projectSprintFilter);
  }, [projectSprintFilter]);

  useEffect(() => {
    localStorage.setItem(MY_OBJECTIVES_SCOPE_STORAGE_KEY, myObjectivesSprintFilter);
  }, [myObjectivesSprintFilter]);

  // Au montage, initialiser les maps de sélection
  useEffect(() => {
    // Initialiser la map des projets sélectionnés
    const projectsMap = {};
    if (selectedProjects.includes("all")) {
      projectsMap.all = true;
    } else {
      selectedProjects.forEach(id => {
        if (id) projectsMap[id] = true;
      });
    }
    setSelectedProjectsMap(projectsMap);

    // Initialiser la map des owners sélectionnés
    const ownersMap = {};
    if (filterOwners.includes("all")) {
      ownersMap.all = true;
    } else {
      filterOwners.forEach(id => {
        if (id) ownersMap[id] = true;
      });
    }
    setSelectedOwnersMap(ownersMap);
  }, []);

  // Au montage, récupérer projets et employés
  useEffect(() => {
    const cachedProjects = readCache("projects_options", CACHE_TTL_MS.projects);
    if (Array.isArray(cachedProjects) && cachedProjects.length > 0) {
      setProjects(cachedProjects);
      if (selectedProjects.length === 0) {
        setSelectedProjects(["all"]);
      }
    }

    const cachedEmployees = readCache("employees", CACHE_TTL_MS.employees);
    if (Array.isArray(cachedEmployees) && cachedEmployees.length > 0) {
      setEmployees(cachedEmployees);
    }

    apiService.get("/projects/options")
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setProjects(list);
        writeCache("projects_options", list);
        if (list.length > 0 && selectedProjects.length === 0) {
          setSelectedProjects(["all"]);
        }
      })
      .catch(() => {
        apiService.get("/projects")
          .then((data) => {
            const list = Array.isArray(data) ? data : [];
            setProjects(list);
            writeCache("projects_options", list);
            if (list.length > 0 && selectedProjects.length === 0) {
              setSelectedProjects(["all"]);
            }
          })
          .catch(console.error);
      });

    apiService.get("/employees")
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setEmployees(list);
        writeCache("employees", list);
      })
      .catch(console.error);
  }, []);

  // Recuperer les slots (format overview), le lien slot->sprints est derive localement
  useEffect(() => {
    const shouldFetchSlots = viewMode === "project" && (selectedProjects.length > 1 || selectedProjects.includes("all"));
    if (!shouldFetchSlots) {
      setSlots([]);
      return;
    }

    const cachedSlots = readCache("slots_overview", CACHE_TTL_MS.slots);
    if (Array.isArray(cachedSlots) && cachedSlots.length > 0) {
      setSlots(cachedSlots);
    }

    apiService.get("/slots")
      .then((slotsData) => {
        const list = Array.isArray(slotsData) ? slotsData : [];
        setSlots(list);
        writeCache("slots_overview", list);
      })
      .catch(console.error);
  }, [viewMode, selectedProjects]);

  // Lors du passage en vue "Par projet", définir par défaut le filtre Owner sur l'utilisateur connecté
  useEffect(() => {
    if (viewMode === "project" && user && filterOwners.length === 0) {
      const employeeId = user?.employee_id;
      if (employeeId !== null && employeeId !== undefined && employeeId !== "") {
        setFilterOwners([String(employeeId)]);
      }
    }
  }, [viewMode, user, filterOwners.length]);

  // Fonction pour récupérer les sprints pour tous les projets sélectionnés
  const fetchSprintsForProjects = useCallback(async () => {
    if (projects.length === 0 && selectedProjects.length === 0) {
      setSprints([]);
      setSprintOptions([]);
      return;
    }

    try {
      const shouldLoadAllProjectsForSprints =
        viewMode === "myObjectives" ||
        selectedProjects.length === 0 ||
        selectedProjects.includes("all");

      const projectIdsBaseline = shouldLoadAllProjectsForSprints
        ? projects.map((p) => p.id)
        : selectedProjects;

      const projectIdsRaw = projectIdsBaseline.length > 0
        ? projectIdsBaseline
        : projects.map(p => p.id);

      const normalizedProjectIds = Array.from(
        new Set(
          projectIdsRaw
            .filter(Boolean)
            .map(id => Number(id))
            .filter(id => !Number.isNaN(id))
        )
      );

      if (normalizedProjectIds.length === 0) {
        setSprints([]);
        setSprintOptions([]);
        return;
      }

      const sortedProjectIds = [...normalizedProjectIds].sort((a, b) => a - b);
      const projectIdsKey = sortedProjectIds.join(",");
      const fetchKey = `${viewMode}-${sprintsRefreshToken}-${projectIdsKey}`;
      if (sprintsFetchKeyRef.current === fetchKey || sprintsFetchInFlightRef.current) {
        return;
      }

      const cacheKey = `sprints:${projectIdsKey}`;
      const today = new Date();
      const hydrateSprints = (incomingSprints) => {
        const safeSprints = Array.isArray(incomingSprints) ? incomingSprints : [];
        const sprintsWithStatus = safeSprints.map((sprint) => {
          const startDate = sprint?.start_date ? new Date(sprint.start_date) : null;
          const endDate = sprint?.end_date ? new Date(sprint.end_date) : null;
          return {
            ...sprint,
            isCurrent: startDate && endDate ? today >= startDate && today <= endDate : false,
          };
        });

        const sprintsMap = sprintsWithStatus.reduce((acc, sprint) => {
          if (sprint && sprint.id != null) {
            acc[sprint.id] = sprint;
          }
          return acc;
        }, {});

        setSprints(sprintsWithStatus);
        setSprintOptions(sprintsWithStatus);
        setAllSprints(prev => ({ ...prev, ...sprintsMap }));

        const storedSprint = localStorage.getItem("objectiveBoard_project_sprintFilter");
        if (storedSprint !== null) {
          setProjectSprintFilter(storedSprint);
        } else {
          const currentSprint = sprintsWithStatus.find(s => s.isCurrent);
          if (currentSprint) {
            setProjectSprintFilter(currentSprint.id.toString());
          } else {
            setProjectSprintFilter("all");
          }
        }
      };

      const cachedSprints = readCache(cacheKey, CACHE_TTL_MS.sprints);
      if (Array.isArray(cachedSprints) && cachedSprints.length > 0) {
        hydrateSprints(cachedSprints);
      }

      sprintsFetchInFlightRef.current = true;
      sprintsFetchKeyRef.current = fetchKey;

      let mergedSprints = [];
      try {
        const bulkData = await apiService.get(`/sprints?project_ids=${projectIdsKey}`);
        if (Array.isArray(bulkData)) {
          mergedSprints = bulkData;
        }
      } catch (bulkError) {
        const fallbackResults = await Promise.all(
          sortedProjectIds.map((projectId) =>
            apiService.get(`/projects/${projectId}/sprints`).catch(() => [])
          )
        );
        mergedSprints = fallbackResults.flat();
      }

      if (sprintsFetchKeyRef.current !== fetchKey) {
        return;
      }

      hydrateSprints(mergedSprints);
      writeCache(cacheKey, mergedSprints);
    } catch (error) {
      console.error("Erreur lors de la récupération des sprints:", error);
    } finally {
      sprintsFetchInFlightRef.current = false;
    }
  }, [projects, selectedProjects, sprintsRefreshToken, viewMode]);

  // En vue "Par projet", récupérer les sprints des projets sélectionnés
  useEffect(() => {
    const shouldFetch =
      (viewMode === "project" && (selectedProjects.length > 0 || projects.length > 0)) ||
      (viewMode === "myObjectives" && projects.length > 0);
    if (shouldFetch) {
      fetchSprintsForProjects();
    }
  }, [viewMode, selectedProjects, projects, sprintsRefreshToken, fetchSprintsForProjects]);

  const fetchObjectivesBulk = useCallback(
    async (projectIds, ownerId = null, options = {}) => {
      const normalizedProjectIds = Array.from(
        new Set(
          (projectIds || [])
            .filter(Boolean)
            .map((id) => Number(id))
            .filter((id) => !Number.isNaN(id))
        )
      );

      const hasOwnerFilter = ownerId !== null && ownerId !== undefined && ownerId !== "";
      if (normalizedProjectIds.length === 0 && !hasOwnerFilter) {
        return [];
      }

      const query = new URLSearchParams();
      if (normalizedProjectIds.length > 0) {
        query.set("project_ids", normalizedProjectIds.join(","));
      }
      if (hasOwnerFilter) {
        query.set("owner_ids", String(ownerId));
      }
      if (Array.isArray(options.statuses) && options.statuses.length > 0) {
        query.set("statuses", options.statuses.join(","));
      }
      if (Number.isInteger(options.limit) && options.limit > 0) {
        query.set("limit", String(options.limit));
      }
      if (Number.isInteger(options.offset) && options.offset >= 0) {
        query.set("offset", String(options.offset));
      }
      if (typeof options.order === "string" && options.order.trim()) {
        query.set("order", options.order.trim());
      }

      try {
        const data = await apiService.get(`/objectives?${query.toString()}`);
        return Array.isArray(data) ? data : [];
      } catch (bulkError) {
        if (normalizedProjectIds.length === 0) {
          return [];
        }
        // Fallback legacy pour compatibilite serveur
        const fallbackResults = await Promise.all(
          normalizedProjectIds.map((projectId) =>
            apiService.get(`/projects/${projectId}/objectives`).catch(() => [])
          )
        );
        let merged = fallbackResults.flat();
        if (ownerId !== null && ownerId !== undefined && ownerId !== "") {
          merged = merged.filter((objective) => String(objective.owner_id) === String(ownerId));
        }
        if (Array.isArray(options.statuses) && options.statuses.length > 0) {
          const allowedStatuses = new Set(options.statuses.map((status) => String(status)));
          merged = merged.filter((objective) => allowedStatuses.has(String(objective.status)));
        }
        if (options.order === "done_at_desc") {
          merged = [...merged].sort((left, right) => {
            const leftDoneAt = left?.done_at ? new Date(left.done_at).getTime() : 0;
            const rightDoneAt = right?.done_at ? new Date(right.done_at).getTime() : 0;
            if (rightDoneAt !== leftDoneAt) return rightDoneAt - leftDoneAt;
            return Number(right?.id || 0) - Number(left?.id || 0);
          });
        }
        if (Number.isInteger(options.offset) && options.offset > 0) {
          merged = merged.slice(options.offset);
        }
        if (Number.isInteger(options.limit) && options.limit > 0) {
          merged = merged.slice(0, options.limit);
        }
        return merged;
      }
    },
    []
  );

  // Recuperation des objectifs pour les projets selectionnes (bulk pour eviter N requetes)
  useEffect(() => {
    let cancelled = false;

    const loadObjectives = async (projectIds, ownerId = null, options = {}) => {
      const normalizedProjectIds = Array.from(
        new Set(
          (projectIds || [])
            .filter(Boolean)
            .map((id) => Number(id))
            .filter((id) => !Number.isNaN(id))
        )
      );

      const hasOwnerScope = ownerId !== null && ownerId !== undefined && ownerId !== "";
      if (normalizedProjectIds.length === 0 && !hasOwnerScope) {
        setObjectives([]);
        setObjectivesLoading(false);
        setDoneObjectivesPage({ offset: 0, hasMore: false, loading: false });
        doneObjectivesScopeRef.current = { projectIds: [], ownerId: null };
        return;
      }

      const loadId = objectivesLoadIdRef.current + 1;
      const loadAllDoneObjectives = options.loadAllDoneObjectives === true;
      objectivesLoadIdRef.current = loadId;
      doneObjectivesScopeRef.current = { projectIds: normalizedProjectIds, ownerId };
      setObjectives([]);
      setObjectivesLoading(true);
      setDoneObjectivesPage({ offset: 0, hasMore: false, loading: true });

      let openObjectivesChunk = [];
      let doneObjectivesChunk = [];
      const isStale = () => cancelled || objectivesLoadIdRef.current !== loadId;
      const commitChunks = () => {
        if (isStale()) return;
        const merged = [...openObjectivesChunk, ...doneObjectivesChunk];
        const deduped = [];
        const seen = new Set();
        merged.forEach((objective) => {
          const key = String(objective?.id ?? "");
          if (!key || seen.has(key)) return;
          seen.add(key);
          deduped.push(objective);
        });
        if (deduped.length > 0) {
          setObjectivesLoading(false);
        }
        setObjectives(deduped);
      };

      const openPromise = fetchObjectivesBulk(normalizedProjectIds, ownerId, {
        statuses: NON_DONE_STATUSES,
      })
        .then((openObjectives) => {
          if (isStale()) return;
          openObjectivesChunk = Array.isArray(openObjectives) ? openObjectives : [];
          commitChunks();
        })
        .catch((error) => {
          if (isStale()) return;
          console.error(error);
          openObjectivesChunk = [];
          commitChunks();
        });

      const donePromise = fetchObjectivesBulk(normalizedProjectIds, ownerId, {
        statuses: ["done"],
        order: "done_at_desc",
        ...(loadAllDoneObjectives
          ? {}
          : {
              limit: DONE_OBJECTIVES_PAGE_SIZE,
              offset: 0,
            }),
      })
        .then((doneObjectives) => {
          if (isStale()) return;
          doneObjectivesChunk = Array.isArray(doneObjectives) ? doneObjectives : [];
          commitChunks();
          setDoneObjectivesPage({
            offset: doneObjectivesChunk.length,
            hasMore: !loadAllDoneObjectives && doneObjectivesChunk.length === DONE_OBJECTIVES_PAGE_SIZE,
            loading: false,
          });
        })
        .catch((error) => {
          if (isStale()) return;
          console.error(error);
          doneObjectivesChunk = [];
          commitChunks();
          setDoneObjectivesPage({ offset: 0, hasMore: false, loading: false });
        });

      await Promise.allSettled([openPromise, donePromise]);
      if (isStale()) return;
      setObjectivesLoading(false);
    };

    if (viewMode === "project") {
      let projectIds = selectedProjects;

      if (selectedProjects.includes("all")) {
        projectIds = projects.map((project) => project.id);
      }

      if (projectIds.length > 0) {
        loadObjectives(projectIds).catch((error) => {
          if (cancelled) return;
          console.error(error);
          setObjectivesLoading(false);
          setDoneObjectivesPage((prev) => ({ ...prev, loading: false }));
        });

        if (selectedProject) {
          setNewObjective((prev) => ({ ...prev, project_id: selectedProject }));
        }
      } else {
        setObjectives([]);
        setObjectivesLoading(false);
        setDoneObjectivesPage({ offset: 0, hasMore: false, loading: false });
        doneObjectivesScopeRef.current = { projectIds: [], ownerId: null };
      }
    } else if (viewMode === "myObjectives" && employee) {
      loadObjectives([], employee.id, {
        loadAllDoneObjectives: true,
      }).catch((error) => {
        if (cancelled) return;
        console.error(error);
        setObjectivesLoading(false);
        setDoneObjectivesPage((prev) => ({ ...prev, loading: false }));
      });
    } else {
      setObjectivesLoading(false);
      setDoneObjectivesPage({ offset: 0, hasMore: false, loading: false });
      doneObjectivesScopeRef.current = { projectIds: [], ownerId: null };
      if (viewMode === "myObjectives") {
        setObjectives([]);
      }
    }

    return () => {
      cancelled = true;
    };
  }, [viewMode, selectedProjects, projects, employee, selectedProject, objectivesRefreshToken, fetchObjectivesBulk]);

  useEffect(() => {
    if (!lastMutation || !lastMutation.metadata) {
      return;
    }
    const entity = (lastMutation.metadata.entity || "").toLowerCase();
    if (entity === "objectives" || entity === "tasks") {
      setObjectivesRefreshToken((value) => value + 1);
      clearCacheByPrefix("sprints:");
      clearCacheByPrefix("slots_overview");
    }
    if (entity === "iterations" || entity === "sprints") {
      setSprintsRefreshToken((value) => value + 1);
      clearCacheByPrefix("sprints:");
      clearCacheByPrefix("slots_overview");
    }
    if (entity === "projects") {
      clearCacheByPrefix("projects_options");
      clearCacheByPrefix("sprints:");
      clearCacheByPrefix("slots_overview");
    }
    if (entity === "employees") {
      clearCacheByPrefix("employees");
    }
    if (entity === "slots") {
      clearCacheByPrefix("slots_overview");
    }
  }, [lastMutation]);

  useEffect(() => {
    if (viewMode !== "myObjectives") {
      setCurrentSprintsMapping({});
      return;
    }
    const currentDate = new Date();
    const mapping = {};
    (sprints || []).forEach((sprint) => {
      if (!sprint?.project_id || !sprint?.start_date || !sprint?.end_date) return;
      const start = new Date(sprint.start_date);
      const end = new Date(sprint.end_date);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
      if (start <= currentDate && currentDate <= end) {
        const key = String(sprint.project_id);
        if (!Array.isArray(mapping[key])) {
          mapping[key] = [];
        }
        mapping[key].push(Number(sprint.id));
      }
    });
    setCurrentSprintsMapping(mapping);
  }, [viewMode, sprints]);

  useEffect(() => {
    const projectId = pendingNewObjective?.project_id;
    if (!projectId) return;
    if (employees.length === 0) return;
    if (projectParticipants[projectId]) return;

    let isCancelled = false;

    const project = projects.find(
      (item) => String(item.id) === String(projectId),
    );
    const circleId = project?.circle_id;
    if (!circleId) {
      setProjectParticipants((prev) => ({
        ...prev,
        [projectId]: [],
      }));
      return () => {
        isCancelled = true;
      };
    }

    apiService
      .get(`circle-focus?parent_id=${circleId}`)
      .then((data) => {
        if (isCancelled) return;
        const focusEntries = Array.isArray(data) ? data : [];
        const participants = buildProjectParticipants(projectId, focusEntries);
        setProjectParticipants((prev) => ({
          ...prev,
          [projectId]: participants,
        }));
      })
      .catch((error) => {
        if (isCancelled) return;
        console.error(
          "Erreur lors de la recuperation du focus projet:",
          error,
        );
        setProjectParticipants((prev) => ({
          ...prev,
          [projectId]: [],
        }));
      });

    return () => {
      isCancelled = true;
    };
  }, [
    pendingNewObjective?.project_id,
    projectParticipants,
    projects,
    buildProjectParticipants,
  ]);

  useEffect(() => {
    if (!user || employees.length === 0) {
      setEmployee(null);
      return;
    }

    const resolvedByEmployeeId =
      user?.employee_id !== null && user?.employee_id !== undefined && user?.employee_id !== ""
        ? employees.find((emp) => String(emp.id) === String(user.employee_id))
        : null;

    if (resolvedByEmployeeId) {
      setEmployee(resolvedByEmployeeId);
      return;
    }

    const normalizedUserEmail =
      typeof user.email === "string" ? user.email.trim().toLowerCase() : "";
    const resolvedByEmail = normalizedUserEmail
      ? employees.find(
          (emp) => String(emp?.email || "").trim().toLowerCase() === normalizedUserEmail
        )
      : null;

    setEmployee(resolvedByEmail || null);
  }, [user, employees]);

  // Mettre à jour les listes des projets et owners sélectionnés
  // quand leurs maps respectives changent
  useEffect(() => {
    // Vérifier s'il y a eu un changement réel
    const currentProjectsAsString = JSON.stringify(selectedProjects.sort());

    let newProjects;
    if (selectedProjectsMap.all) {
      newProjects = ["all"];
    } else {
      newProjects = Object.keys(selectedProjectsMap)
        .filter(id => selectedProjectsMap[id] && id !== "all");

      if (newProjects.length === 0) {
        newProjects = ["all"];
      }
    }

    const newProjectsAsString = JSON.stringify(newProjects.sort());

    if (currentProjectsAsString !== newProjectsAsString) {
      setSelectedProjects(newProjects);
    }
  }, [selectedProjectsMap]);

  useEffect(() => {
    // Vérifier s'il y a eu un changement réel
    const currentOwnersAsString = JSON.stringify(filterOwners.sort());

    let newOwners;
    if (selectedOwnersMap.all) {
      newOwners = ["all"];
    } else {
      newOwners = Object.keys(selectedOwnersMap)
        .filter(id => selectedOwnersMap[id] && id !== "all");

      if (newOwners.length === 0) {
        newOwners = ["all"];
      }
    }

    const newOwnersAsString = JSON.stringify(newOwners.sort());

    if (currentOwnersAsString !== newOwnersAsString) {
      setFilterOwners(newOwners);
    }
  }, [selectedOwnersMap]);

  // --- NOUVEAU: Handlers pour les suggestions de titre ---
  const handleRequestSuggestion = async (objectiveId, objectiveName) => {
    const objectiveToUpdate = objectives.find(t => t.id === objectiveId);
    if (!objectiveToUpdate) return;
    const projectId = objectiveToUpdate.project_id;

    try {
      const data = await apiService.suggestObjectiveTitle(projectId, objectiveName);
      if (data && data.suggested_title) {
        // Appliquer directement la suggestion
        const updatedObjective = await apiService.patch(`/projects/${projectId}/objectives/${objectiveId}`, { name: data.suggested_title });
        setObjectives(prev => prev.map(t => (t.id === objectiveId ? { ...t, name: updatedObjective.name } : t)));
      }
    } catch (error) {
      console.error("Erreur lors de la suggestion/mise à jour du titre:", error);
      // Gérer l'erreur (par ex. avec un message à l'utilisateur)
    }
  };
  // --- FIN NOUVEAU ---

  // Fonction pour basculer la sélection d'un projet
  const toggleProjectSelection = (projectId) => {
    if (projectId === "all") {
      // Si "Tous" est sélectionné, désélectionner tous les autres
      setSelectedProjectsMap({ all: true });
    } else {
      setSelectedProjectsMap(prev => {
        const newMap = { ...prev, all: false, [projectId]: !prev[projectId] };
        // Vérifier si aucun projet n'est sélectionné, alors sélectionner "Tous"
        const hasSelected = Object.keys(newMap).some(id => id !== "all" && newMap[id]);
        if (!hasSelected) {
          newMap.all = true;
        }
        return newMap;
      });
    }
  };

  // Fonction pour effacer tous les filtres de projets
  const clearProjectsFilter = () => {
    setSelectedProjectsMap({ all: true });
    setActiveFilterDropdown(null);
    setProjectSearch("");
  };

  // Fonction pour basculer la sélection d'un owner
  const toggleOwnerSelection = (ownerId) => {
    if (ownerId === "all") {
      // Si "Tous" est sélectionné, désélectionner tous les autres
      setSelectedOwnersMap({ all: true });
    } else {
      // Sinon, désélectionner "Tous" et basculer la sélection de l'owner
      setSelectedOwnersMap(prev => {
        const newMap = { ...prev, all: false, [ownerId]: !prev[ownerId] };
        // Vérifier si aucun owner n'est sélectionné, alors sélectionner "Tous"
        const hasSelected = Object.keys(newMap).some(id => id !== "all" && newMap[id]);
        if (!hasSelected) {
          newMap.all = true;
        }
        return newMap;
      });
    }
  };

  // Fonction pour effacer tous les filtres d'owners
  const clearOwnersFilter = () => {
    setSelectedOwnersMap({ all: true });
    setActiveFilterDropdown(null);
    setOwnerSearch("");
  };

  // Fonctions utilitaires
  const getOwnerName = (owner_id) => {
    const emp = employees.find(e => e.id === owner_id);
    return emp ? emp.name.split(" ")[0] : "-";
  };

  const getSprintName = (sprint_id) => {
    if (sprint_id != null) {
      if (viewMode === "project") {
        const sp = sprintOptions.find(s => s.id === sprint_id);
        return sp ? (sp.description ? sp.description : `Sprint ${sp.id}`) : `Sprint ${sprint_id}`;
      } else { // Pour "Mes objectifs"
        const sp = allSprints[sprint_id];
        return sp ? (sp.description ? sp.description : `Sprint ${sp.id}`) : `Sprint ${sprint_id}`;
      }
    }
    return "-- Aucun --";
  };

  const getProjectName = (project_id) => {
    const proj = projectById.get(String(project_id));
    return proj ? proj.name : "";
  };

  const getProjectPriorityInfoForId = (project_id) => {
    const project = projectById.get(String(project_id));
    return getProjectPriorityInfo(project?.pilotage_priority);
  };

  // Edition inline
  const enableEditing = (objective, field) => {
    setEditing({
      objectiveId: objective.id,
      field,
      value: field === "sprint_id"
        ? (objective.sprint_id !== null ? objective.sprint_id.toString() : "")
        : (objective[field] ?? "")
    });
  };

  const handleEditingChange = (e) => {
    setEditing({ ...editing, value: e.target.value });
  };

  // --- NOUVELLE FONCTION POUR LES SELECTS ---
  const handleSelectChange = async (objectiveId, field, value) => {
    const objectiveToUpdate = objectives.find(t => t.id === objectiveId);
    if (!objectiveToUpdate) {
      console.error("Impossible de trouver l'objectif à mettre à jour:", objectiveId);
      return;
    }
    const projectId = objectiveToUpdate.project_id;
    const fieldValue = value === "" ? null : Number(value);

    try {
      const updatedObjective = await apiService.patch(
        `/projects/${projectId}/objectives/${objectiveId}`,
        { [field]: fieldValue }
      );
      setObjectives(prev =>
        prev.map(t => (t.id === objectiveId ? { ...t, [field]: updatedObjective[field] } : t))
      );
    } catch (error) {
      console.error(error);
    }
    setEditing({ objectiveId: null, field: null, value: "" }); // Quitter le mode édition
  };
  // --- FIN NOUVELLE FONCTION ---

  const handleEditingBlur = async () => {
    const { objectiveId, field, value } = editing;
    if (!objectiveId || field === "sprint_id" || field === "owner_id") {
      setEditing({ objectiveId: null, field: null, value: "" });
      return;
    }

    const objectiveToUpdate = objectives.find(t => t.id === objectiveId);
    if (!objectiveToUpdate) {
      console.error("Impossible de trouver l'objectif à mettre à jour:", objectiveId);
      setEditing({ objectiveId: null, field: null, value: "" });
      return;
    }
    const projectId = objectiveToUpdate.project_id;
    const fieldValue = field === "effort" ? Number(value) : value;

    try {
      const updatedObjective = await apiService.patch(
        `/projects/${projectId}/objectives/${objectiveId}`,
        { [field]: fieldValue }
      );
      setObjectives(prev =>
        prev.map(t => t.id === objectiveId ? { ...t, [field]: updatedObjective[field] } : t)
      );
    } catch (error) {
      console.error(error);
    }

    setEditing({ objectiveId: null, field: null, value: "" });
  };


  // Drag & drop
  const handleDragStart = (e, objective) => {
    setDraggedObjective(objective);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (!draggedObjective) return;

    // Créer une copie locale de l'objectif avec le nouveau statut
    const updatedObjective = { ...draggedObjective, status: newStatus };

    // Mettre à jour l'état immédiatement pour une UI réactive
    setObjectives(prev =>
      prev.map(t => t.id === draggedObjective.id ? updatedObjective : t)
    );

    if (draggedObjective.status !== newStatus) {
      const projectId = draggedObjective.project_id; // On utilise directement l'ID du projet de l'objectif

      try {
        await apiService.patch(
          `/projects/${projectId}/objectives/${draggedObjective.id}`,
          { status: newStatus }
        );
        // La mise à jour de l'état a déjà été faite au-dessus
      } catch (error) {
        console.error(error);
        // En cas d'erreur, rétablir l'état précédent
        setObjectives(prev =>
          prev.map(t => t.id === draggedObjective.id ? draggedObjective : t)
        );
      }
    }
    setDraggedObjective(null);
  };

  // --- MODIFIÉ: Logique de changement pour le nouvel objectif ---
  const handleNewObjectiveChange = (field, value) => {
    if (field === "name") {
      // Si le nom change, on réinitialise la suggestion de titre, mais pas les tokens.
      setPredictedTitle(null);
      setTitleSuggestionDismissed(false);
      setMuseTitleRewrite(null);
    }
    // Si le champ modifié est project_id, on met à jour également le sprint_id
    if (field === "project_id" && value) {
      const projectId = value;
      let latestSprintId = null;

      // Trouver le sprint le plus récent du projet sélectionné
      const projectSprints = sprints.filter(s => String(s.project_id) === String(projectId));
      if (projectSprints.length > 0) {
        const sortedSprints = [...projectSprints].sort((a, b) =>
          new Date(b.start_date) - new Date(a.start_date)
        );
        latestSprintId = sortedSprints[0].id;
      }

      // Mettre à jour à la fois project_id et sprint_id
      setPendingNewObjective(prev => ({
        ...prev,
        project_id: projectId,
        sprint_id: latestSprintId
      }));
    } else {
      // Pour les autres champs, comportement normal
      setPendingNewObjective(prev => ({
        ...prev,
        [field]: field === "effort"
          ? (value === '' ? '' : Number(value))
          : value
      }));
    }
  };
  // --- FIN MODIFIÉ ---

  const handleAddSubTask = () => {
    setPendingNewObjective(prev => {
      if (!prev) return prev;
      const existing = Array.isArray(prev.subTasks) ? prev.subTasks : [];
      return {
        ...prev,
        subTasks: [...existing, { name: "", owner_id: "" }],
      };
    });
  };

  const handleSubTaskChange = (index, field, value) => {
    setPendingNewObjective(prev => {
      if (!prev) return prev;
      const existing = Array.isArray(prev.subTasks) ? [...prev.subTasks] : [];
      if (!existing[index]) {
        existing[index] = { name: "", owner_id: "" };
      }
      const nextValue =
        field === "owner_id"
          ? (value === "" ? "" : Number(value))
          : value;
      existing[index] = {
        ...existing[index],
        [field]: nextValue,
      };
      return {
        ...prev,
        subTasks: existing,
      };
    });
  };

  const handleRemoveSubTask = (index) => {
    setPendingNewObjective(prev => {
      if (!prev) return prev;
      const existing = Array.isArray(prev.subTasks) ? [...prev.subTasks] : [];
      if (index < 0 || index >= existing.length) {
        return prev;
      }
      existing.splice(index, 1);
      return {
        ...prev,
        subTasks: existing,
      };
    });
  };

  // --- MODIFICATION: Fusion des prédictions IA ---
  const handleAiPredictions = async (name) => {
    if (!pendingNewObjective?.project_id || !name) return;

    // Prédiction des tokens
    setPredictingTokens(true);
    try {
      const data = await apiService.get(
        `/projects/${pendingNewObjective.project_id}/objectives/predict_tokens?name=${encodeURIComponent(name)}`
      );
      setPredictedTokens(data.predicted_tokens);
    } catch (error) {
      console.error("Erreur de prédiction IA des tokens:", error);
    } finally {
      setPredictingTokens(false);
    }

    // Reformulation du titre par Muse : imposée (animation dans le formulaire), sauf si le nom a changé entre-temps.
    if (museTitleRewrite && museTitleRewrite.to === name) return;
    setPredictingTitle(true);
    try {
      const data = await apiService.suggestObjectiveTitle(pendingNewObjective.project_id, name);
      if (data.suggested_title && data.suggested_title !== name && pendingNameRef.current === name) {
        setPendingNewObjective((prev) => (prev && prev.name === name ? { ...prev, name: data.suggested_title } : prev));
        setMuseTitleRewrite({ from: name, to: data.suggested_title });
      }
    } catch (error) {
      console.error("Erreur de suggestion de titre IA:", error);
    } finally {
      setPredictingTitle(false);
    }
  };

  // --- MODIFICATION: Création d'objectif simplifiée ---
  const handleCreateNewObjective = () => {
    setPredictedTokens(null);
    setPredictingTokens(false);
    setPredictedTitle(null);
    setPredictingTitle(false);
    setTitleSuggestionDismissed(false);
    setMuseTitleRewrite(null);

    // Si un seul projet est sélectionné, on le pré-remplit. Sinon, le champ reste vide.
    let defaultProjectId = "";
    if (selectedProjects.length === 1 && !selectedProjects.includes("all")) {
      defaultProjectId = selectedProjects[0];
    }

    let latestSprintId = null;
    if (defaultProjectId) {
      const projectSprints = sprints.filter(s => String(s.project_id) === String(defaultProjectId));
      if (projectSprints.length > 0) {
        const sortedSprints = [...projectSprints].sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
        latestSprintId = sortedSprints[0].id;
      }
    }

    const defaultObjective = {
      project_id: defaultProjectId,
      name: "",
      description: "",
      sprint_id: latestSprintId,
      effort: 0,
      owner_id: "",
      status: "not_started",
      subTasks: []
    };
    setPendingNewObjective(defaultObjective);
    setShowAddForm(true);
  };

  // Le bouton appellera toujours handleCreateNewObjective maintenant
  const handleAddObjectiveClick = () => {
    handleCreateNewObjective();
  };
  // --- FIN MODIFICATION ---

  // Sauvegarde d'un nouvel objectif
  const handleSaveNewObjective = async (e) => {
    if (e) e.preventDefault();
    if (!pendingNewObjective || !pendingNewObjective.project_id || !pendingNewObjective.name) return;

    const { subTasks = [], ...objectiveDraft } = pendingNewObjective;
    const projectId = Number(objectiveDraft.project_id);
    if (!projectId) return;

    const objectiveToSave = {
      ...objectiveDraft,
      project_id: projectId,
      sprint_id: objectiveDraft.sprint_id ? Number(objectiveDraft.sprint_id) : null,
      owner_id:
        objectiveDraft.owner_id === "" || objectiveDraft.owner_id === null
          ? null
          : Number(objectiveDraft.owner_id),
      effort:
        objectiveDraft.effort === "" || objectiveDraft.effort === null
          ? 0
          : Number(objectiveDraft.effort),
    };

    try {
      const createdObjective = await apiService.post(
        `/projects/${projectId}/objectives`,
        objectiveToSave
      );

      const sanitizedSubTasks = Array.isArray(subTasks)
        ? subTasks
          .map(task => ({
            name: task?.name ? task.name.trim() : "",
            owner_id:
              task?.owner_id === "" || task?.owner_id === null
                ? null
                : Number(task.owner_id),
          }))
          .filter(task => task.name.length > 0)
        : [];

      let createdTasks = [];
      if (sanitizedSubTasks.length > 0) {
        const taskResults = await Promise.all(
          sanitizedSubTasks.map(task =>
            apiService
              .post(`/objectives/${createdObjective.id}/tasks`, {
                name: task.name,
                owner_id: task.owner_id,
                objective_id: createdObjective.id,
                project_id: createdObjective.project_id ?? projectId,
              })
              .catch(error => {
                console.error("Erreur lors de la création d'une sous-tâche:", error);
                return null;
              })
          )
        );
        createdTasks = taskResults.filter(Boolean);
      }

      const enrichedObjective =
        createdTasks.length > 0
          ? {
            ...createdObjective,
            tasks: Array.isArray(createdObjective.tasks)
              ? [...createdObjective.tasks, ...createdTasks]
              : createdTasks,
          }
          : createdObjective;

      // Animer l'objectif qui entre dans le backlog
      setNewObjectiveAnimating(true);

      // Après l'animation, ajouter l'objectif au backlog et réinitialiser
      setTimeout(() => {
        setObjectives(prev => [...prev, enrichedObjective]);
        setShowAddForm(false);
        setPendingNewObjective(null);
        setNewObjectiveAnimating(false);
      }, 500); // Durée de l'animation
    } catch (error) {
      console.error(error);
      // En cas d'erreur, fermer directement
      setShowAddForm(false);
      setPendingNewObjective(null);
    }
  };

  // Fonction pour fermer le formulaire d'ajout
  const handleCancelNewObjective = () => {
    setShowAddForm(false);
    setPendingNewObjective(null);
  };

  const handleDeleteObjective = async (objectiveId) => {
    try {
      // Déterminer l'ID du projet à partir de l'objectif elle-même
      const objectiveToDelete = objectives.find(t => t.id === objectiveId);
      if (!objectiveToDelete) return;
      const projectId = objectiveToDelete.project_id;
      await apiService.delete(`/projects/${projectId}/objectives/${objectiveId}`);
      // Mettre à jour la liste locale des objectifs
      setObjectives(prev => prev.filter(t => t.id !== objectiveId));
    } catch (error) {
      console.error(error);
    }
  };

  // Fonction pour ouvrir le popup
  const openCommentPopup = (objective) => {
    setCommentObjective(objective);
    setCommentText(objective.comment || "");
  };

  // Fermeture du popup
  const closeCommentPopup = () => {
    setCommentObjective(null);
    setCommentText("");
  };

  // Sauvegarder le commentaire
  const handleCommentSave = async () => {
    if (!commentObjective) return;
    const projectId = commentObjective.project_id;
    try {
      await apiService.patch(
        `/projects/${projectId}/objectives/${commentObjective.id}`,
        { comment: commentText }
      );
      setObjectives((prev) =>
        prev.map((t) =>
          t.id === commentObjective.id ? { ...t, comment: commentText } : t
        )
      );
      closeCommentPopup();
    } catch (error) {
      console.error(error);
    }
  };

  const getOwnerInitials = (owner_id) => {
    const emp = employees.find(e => e.id === owner_id);
    if (emp && emp.email) {
      // Extraction de la partie avant le @ (exemple "remy.cartier")
      const emailName = emp.email.split('@')[0];
      const nameParts = emailName.split('.');

      // Premier caractère de chaque partie du nom
      return nameParts.map(part => part[0]?.toUpperCase() || '').join('');
    }
    return "";
  };

  const handleNewObjectiveKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveNewObjective();
    }
  };

  const handleCommentTextChange = (e) => {
    setCommentText(e.target.value);
  };

  // --- Filtrage des objectifs (final step before rendering) ---
  let filteredObjectives = objectives;

  // 1. Apply Owner/Sprint/Slot Filters
  if (viewMode === "project") {
    // Filtrer par owner(s)
    if (!selectedOwnersMap.all) {
      const selectedOwnerIds = Object.keys(selectedOwnersMap).filter(id => selectedOwnersMap[id]);
      if (selectedOwnerIds.length > 0) {
        filteredObjectives = filteredObjectives.filter(t => selectedOwnerIds.includes(String(t.owner_id)));
      }
    }

    // Filtrer par sprint ou slot
    if (selectedProjects.length === 1 && !selectedProjects.includes("all")) {
      // En mode un seul projet sélectionné: filtrer par sprint
      if (projectSprintFilter !== "all") {
        filteredObjectives = filteredObjectives.filter(
          (t) => t.is_milestone || String(t.sprint_id) === projectSprintFilter,
        );
      }
    } else if (selectedSlot !== "all") {
      // En mode multi-projet ou "tous": filtrer par slot via les sprints recoupes avec la periode du slot
      const sprintIdsForSlot = slotSprintIdsMap[String(selectedSlot)];
      if (Array.isArray(sprintIdsForSlot) && sprintIdsForSlot.length > 0) {
        filteredObjectives = filteredObjectives.filter((t) =>
          t.sprint_id && sprintIdsForSlot.includes(String(t.sprint_id))
        );
      } else if (Array.isArray(sprintIdsForSlot) && sprints.length > 0) {
        filteredObjectives = [];
      }
    }
  } else if (viewMode === "myObjectives") {
    if (myObjectivesSprintFilter === "enCours") {
      filteredObjectives = filteredObjectives.filter(t => {
        const currentProjectSprintIds = currentSprintsMapping[t.project_id]; // Ceci est maintenant un tableau
        // Vérifier si currentProjectSprintIds est un tableau et si t.sprint_id est l'un d'eux
        return Array.isArray(currentProjectSprintIds) && currentProjectSprintIds.includes(Number(t.sprint_id));
      });
    }
    // Si myObjectivesSprintFilter === "all", aucun objectif de sprint n'est appliquée ici.
  }

  // 2. Apply Search Term Filter
  if (searchTerm.trim() !== "") {
    const searchTermLower = searchTerm.toLowerCase();
    filteredObjectives = filteredObjectives.filter(objective => {
      const projectName = getProjectName(objective.project_id) || ''; // Get project name
      return (
        objective.name?.toLowerCase().includes(searchTermLower) ||
        projectName.toLowerCase().includes(searchTermLower)
      );
    });
  }

  // --- NOUVEAU: Handler pour mettre à jour un objectif dans la liste ---
  filteredObjectives = [...filteredObjectives].sort((a, b) => {
    const projectPriorityA = getProjectPriorityValue(projectById.get(String(a.project_id)));
    const projectPriorityB = getProjectPriorityValue(projectById.get(String(b.project_id)));
    if (projectPriorityA !== projectPriorityB) {
      return projectPriorityA - projectPriorityB;
    }
    return 0;
  });

  const handleUpdateObjectiveInList = (updatedObjective) => {
    setObjectives(prev =>
      prev.map(obj => obj.id === updatedObjective.id ? updatedObjective : obj)
    );
  };

  const handleToggleObjectiveSubscription = async (objective) => {
    if (!objective) {
      return;
    }
    const projectId = objective.project_id;
    if (!projectId) {
      console.warn("Impossible de déterminer le projet pour l'objectif suivi.");
      return;
    }
    const currentUserId = user?.id;
    if (currentUserId == null) {
      console.warn("Utilisateur non connecté : abonnement aux objectifs ignoré.");
      return;
    }
    const isSubscribed = Array.isArray(objective.subscriber_ids)
      && objective.subscriber_ids.some((id) => String(id) === String(currentUserId));
    try {
      const response = isSubscribed
        ? await apiService.unsubscribeFromObjective(projectId, objective.id)
        : await apiService.subscribeToObjective(projectId, objective.id);
      handleUpdateObjectiveInList(response);
    } catch (error) {
      console.error("Erreur lors du changement d'abonnement à l'objectif :", error);
    }
  };

  const handleLoadMoreDoneObjectives = useCallback(async () => {
    if (isSearchActive || doneObjectivesPage.loading || !doneObjectivesPage.hasMore) {
      return;
    }

    const scope = doneObjectivesScopeRef.current;
    if (!Array.isArray(scope.projectIds) || scope.projectIds.length === 0) {
      return;
    }

    setDoneObjectivesPage((prev) => ({ ...prev, loading: true }));

    try {
      const nextDoneObjectives = await fetchObjectivesBulk(scope.projectIds, scope.ownerId, {
        statuses: ["done"],
        order: "done_at_desc",
        limit: DONE_OBJECTIVES_PAGE_SIZE,
        offset: doneObjectivesPage.offset,
      });

      setObjectives((prev) => {
        const existingIds = new Set(prev.map((objective) => String(objective.id)));
        const deduped = nextDoneObjectives.filter(
          (objective) => !existingIds.has(String(objective.id))
        );
        return [...prev, ...deduped];
      });

      setDoneObjectivesPage((prev) => ({
        offset: prev.offset + nextDoneObjectives.length,
        hasMore: nextDoneObjectives.length === DONE_OBJECTIVES_PAGE_SIZE,
        loading: false,
      }));
    } catch (error) {
      console.error(error);
      setDoneObjectivesPage((prev) => ({ ...prev, loading: false }));
    }
  }, [isSearchActive, doneObjectivesPage.loading, doneObjectivesPage.hasMore, doneObjectivesPage.offset, fetchObjectivesBulk]);

  const handleLoadMoreColumn = useCallback(
    (status) => {
      if (isSearchActive) {
        return;
      }
      if (status === "done") {
        handleLoadMoreDoneObjectives();
        return;
      }
      setVisibleObjectivesCount((prev) => {
        const current = prev[status] ?? COLUMN_BATCH_SIZE;
        return {
          ...prev,
          [status]: current + COLUMN_BATCH_SIZE
        };
      });
    },
    [isSearchActive, handleLoadMoreDoneObjectives]
  );

  const statusBuckets = useMemo(() => {
    const buckets = {
      not_started: [],
      in_progress: [],
      blocked: [],
      done: []
    };
    filteredObjectives.forEach((objective) => {
      const bucketKey =
        objective.status && buckets[objective.status] ? objective.status : "not_started";
      buckets[bucketKey].push(objective);
    });
    return buckets;
  }, [filteredObjectives]);

  const totalObjectives = filteredObjectives.length;
  const completionRate = totalObjectives
    ? Math.round((statusBuckets.done.length / totalObjectives) * 100)
    : 0;

  const selectedProjectsLabel = useMemo(() => {
    if (viewMode === "myObjectives") {
      return "Vos missions personnelles";
    }
    if (selectedProjects.includes("all")) {
      return "Tous les projets actifs";
    }
    const names = selectedProjects
      .map((id) => getProjectName(id))
      .filter(Boolean);
    if (names.length === 0) {
      return "Projets sélectionnés";
    }
    if (names.length === 1) {
      return names[0];
    }
    return `${names[0]} +${names.length - 1}`;
  }, [selectedProjects, viewMode, projects]);

  const activeOwnersCount = useMemo(() => {
    const owners = new Set(
      filteredObjectives
        .map((objective) => objective.owner_id)
        .filter(
          (ownerId) =>
            ownerId !== null &&
            ownerId !== undefined &&
            ownerId !== ""
        )
    );
    return owners.size;
  }, [filteredObjectives]);

  const sprintCoverageCount = useMemo(() => {
    const sprintsSet = new Set(
      filteredObjectives
        .map((objective) => objective.sprint_id)
        .filter(
          (sprintId) =>
            sprintId !== null &&
            sprintId !== undefined &&
            sprintId !== ""
        )
    );
    return sprintsSet.size;
  }, [filteredObjectives]);

  const heroDescription =
    viewMode === "project"
      ? "Orchestrez les initiatives clés et détectez les frictions avant qu'elles ne bloquent la livraison."
      : "Gardez une vision claire de vos engagements personnels pour ce sprint.";
  const heroIsCompact = isSearchActive;

  const heroTabs = [
    {
      id: "project",
      label: "Par projet",
      description: "Vue portefeuille",
      active: viewMode === "project",
      onClick: () => handleViewModeChange("project"),
      Icon: LayoutGrid
    },
    {
      id: "myObjectives",
      label: "Mes objectifs",
      description: "Focus personnel",
      active: viewMode === "myObjectives",
      onClick: () => handleViewModeChange("myObjectives"),
      Icon: UserRound
    },
  ];

  const kanbanColumns = [
    { title: "À Faire", status: "not_started", objectives: statusBuckets.not_started },
    { title: "En cours", status: "in_progress", objectives: statusBuckets.in_progress },
    { title: "Bloqué", status: "blocked", objectives: statusBuckets.blocked },
    { title: "Terminé", status: "done", objectives: statusBuckets.done }
  ];

  const projectFilterLabel = useMemo(() => {
    if (selectedProjects.includes("all")) return "Tous les projets";
    if (selectedProjects.length === 1) {
      return getProjectName(selectedProjects[0]) || "1 projet";
    }
    return `${selectedProjects.length} projets`;
  }, [selectedProjects, projects]);

  const ownerFilterLabel = useMemo(() => {
    if (filterOwners.includes("all") || filterOwners.length === 0) {
      return "Tous les owners";
    }
    if (filterOwners.length === 1) {
      return getOwnerName(filterOwners[0]) || "1 owner";
    }
    return `${filterOwners.length} owners`;
  }, [filterOwners, employees]);

  const slotFilterLabel = useMemo(() => {
    if (selectedSlot === "all") return "Tous les slots";
    const slot = slots.find((s) => String(s.id) === String(selectedSlot));
    if (slot) {
      const label = slot.name || slot.label || slot.title || `Slot ${slot.id}`;
      return isSlotCurrent(slot) ? `${label} (En cours)` : label;
    }
    return "Slot filtré";
  }, [selectedSlot, slots]);

  const projectSelectionSummary = useMemo(() => {
    if (selectedProjects.includes("all")) return "";
    const names = selectedProjects
      .map((id) => {
        const project = projects.find((p) => String(p.id) === String(id));
        return project?.name || "";
      })
      .filter(Boolean);
    if (!names.length) return "";
    if (names.length <= 3) return names.join(", ");
    return `${names.slice(0, 3).join(", ")} +${names.length - 3}`;
  }, [selectedProjects, projects]);

  const ownerSelectionSummary = useMemo(() => {
    if (filterOwners.includes("all") || filterOwners.length === 0) return "";
    const names = filterOwners
      .map((id) => {
        const owner = employees.find((e) => String(e.id) === String(id));
        if (!owner?.name) return "";
        const firstName = owner.name.split(" ")[0];
        return firstName || owner.name;
      })
      .filter(Boolean);
    if (!names.length) return "";
    if (names.length <= 3) return names.join(", ");
    return `${names.slice(0, 3).join(", ")} +${names.length - 3}`;
  }, [filterOwners, employees]);

  const hasFilterSummary = Boolean(projectSelectionSummary || ownerSelectionSummary);

  const displayedColumns = kanbanColumns.map((column) => {
    const isDoneColumn = column.status === "done";
    const limit = visibleObjectivesCount[column.status] ?? COLUMN_BATCH_SIZE;
    const showFullColumn = isSearchActive || viewMode === "myObjectives";
    const objectivesSubset = isSearchActive
      ? column.objectives
      : (showFullColumn || isDoneColumn ? column.objectives : column.objectives.slice(0, limit));
    const hasMore = showFullColumn
      ? false
      : isDoneColumn
      ? (!isSearchActive && doneObjectivesPage.hasMore)
      : (!isSearchActive && column.objectives.length > objectivesSubset.length);
    return {
      ...column,
      objectives: objectivesSubset,
      total: column.objectives.length,
      hasMore
    };
  });

  // ... (useEffect pour charger les objectifs, etc.)

  // ... (reste des fonctions)

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 pb-16">
      <div className="mx-auto flex w-full max-w-[2100px] flex-col gap-5 px-3 py-4 sm:px-5 lg:px-10 xl:px-12 2xl:max-w-[2400px] 2xl:px-16">
        <section
          className={`relative rounded-3xl bg-gradient-to-r from-[#1f1632] via-[#2a1e4c] to-[#103c64] text-white shadow-2xl ${heroIsCompact ? "px-4 py-5 sm:px-6" : "px-6 py-8 sm:px-8"
            }`}
        >
          <div className="pointer-events-none absolute inset-0 opacity-60">
            <div className="h-full w-full bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.25),_transparent_60%)]" />
          </div>
          <div className="relative z-10 flex flex-col gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1 text-[10px] font-semibold uppercase tracking-[0.3em]">
              <MapPin className="h-4 w-4" />
              Mission Control
            </span>
            <div className="flex flex-col gap-1 lg:flex-row lg:items-center lg:justify-between lg:gap-3">
              <h1 className="text-3xl font-semibold tracking-tight sm:text-[2.25rem]">
                Backlog des objectifs
              </h1>
              <p className="text-sm text-white/80 lg:text-base">
                {heroDescription}
              </p>
            </div>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2 text-sm font-semibold text-white/90">
                {heroTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={tab.onClick}
                    className={`rounded-full px-4 py-2 transition ${tab.active
                      ? "bg-white text-slate-900 shadow"
                      : "bg-white/10 text-white/80 hover:bg-white/20"
                      }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {hasFilterSummary && (
                <div className="flex flex-wrap justify-end gap-3 text-xs text-white/80">
                  {projectSelectionSummary && (
                    <div className="flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1">
                      <div className="flex flex-col leading-tight">
                        <span className="text-[10px] uppercase tracking-[0.3em] text-white/60">
                          Projets
                        </span>
                        <span className="text-sm font-medium text-white">
                          {projectSelectionSummary}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={clearProjectsFilter}
                        className="rounded-full bg-white/20 p-1 text-white hover:bg-white/30"
                        aria-label="Réinitialiser les projets filtrés"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  {ownerSelectionSummary && (
                    <div className="flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1">
                      <div className="flex flex-col leading-tight">
                        <span className="text-[10px] uppercase tracking-[0.3em] text-white/60">
                          Owners
                        </span>
                        <span className="text-sm font-medium text-white">
                          {ownerSelectionSummary}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={clearOwnersFilter}
                        className="rounded-full bg-white/20 p-1 text-white hover:bg-white/30"
                        aria-label="Réinitialiser les owners filtrés"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:items-center">
                <div className="flex flex-1 items-center rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white/80 shadow-inner backdrop-blur">
                  <Search className="mr-3 h-4 w-4 text-white/60" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Rechercher un objectif, projet, owner..."
                    className="w-full border-none bg-transparent text-sm text-white placeholder:text-white/50 focus:outline-none"
                  />
                </div>
                <div className="flex flex-wrap gap-2 text-xs font-semibold text-white/90">
                  {viewMode === "myObjectives" && (
                    <div className="inline-flex overflow-hidden rounded-full border border-white/20 bg-white/10">
                      {[
                        { key: "all", label: "Tous" },
                        { key: "enCours", label: "Sprint en cours" },
                      ].map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => setMyObjectivesSprintFilter(option.key)}
                          className={`px-3 py-2 transition ${
                            myObjectivesSprintFilter === option.key
                              ? "bg-white text-slate-800"
                              : "text-white/80 hover:bg-white/10"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {viewMode === "project" && (
                    <>
                  <div className="relative" ref={projectDropdownRef}>
                    <button
                      type="button"
                      onClick={() =>
                        setActiveFilterDropdown((prev) => (prev === "project" ? null : "project"))
                      }
                      className={`rounded-full border border-white/20 px-3 py-2 transition ${activeFilterDropdown === "project" ? "bg-white text-slate-800" : "bg-white/10"
                        }`}
                    >
                      Projets • {projectFilterLabel}
                    </button>
                    {activeFilterDropdown === "project" && (
                      <div className="absolute right-0 z-50 mt-2 w-72 rounded-2xl bg-white p-4 text-slate-700 shadow-2xl">
                        <div className="relative mb-2">
                          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            value={projectSearch}
                            onChange={(e) => setProjectSearch(e.target.value)}
                            placeholder="Rechercher un projet"
                            className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-indigo-400 focus:outline-none"
                          />
                        </div>
                        <div className="max-h-60 overflow-y-auto text-sm">
                          <label className="flex cursor-pointer items-center gap-2 rounded-xl px-2 py-1 hover:bg-slate-50">
                            <input
                              type="checkbox"
                              checked={selectedProjectsMap.all || false}
                              onChange={() => toggleProjectSelection("all")}
                            />
                            <span>Tous les projets</span>
                          </label>
                          {filteredProjects.map((p) => {
                            const priorityInfo = getProjectPriorityInfo(p.pilotage_priority);
                            return (
                              <label
                                key={p.id}
                                className="flex cursor-pointer items-center gap-2 rounded-xl px-2 py-1 hover:bg-slate-50"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedProjectsMap[p.id] || false}
                                  onChange={() => toggleProjectSelection(p.id)}
                                />
                                <span className="min-w-0 flex-1 truncate">{p.name}</span>
                                {priorityInfo && (
                                  <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${priorityInfo.className}`}>
                                    P{priorityInfo.value}
                                  </span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="relative" ref={ownerDropdownRef}>
                    <button
                      type="button"
                      onClick={() =>
                        setActiveFilterDropdown((prev) => (prev === "owner" ? null : "owner"))
                      }
                      className={`rounded-full border border-white/20 px-3 py-2 transition ${activeFilterDropdown === "owner" ? "bg-white text-slate-800" : "bg-white/10"
                        }`}
                    >
                      Owners • {ownerFilterLabel}
                    </button>
                    {activeFilterDropdown === "owner" && (
                      <div className="absolute right-0 z-50 mt-2 w-64 rounded-2xl bg-white p-4 text-slate-700 shadow-2xl">
                        <div className="relative mb-2">
                          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            value={ownerSearch}
                            onChange={(e) => setOwnerSearch(e.target.value)}
                            placeholder="Rechercher un owner"
                            className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-indigo-400 focus:outline-none"
                          />
                        </div>
                        <div className="max-h-56 overflow-y-auto text-sm">
                          <label className="flex cursor-pointer items-center gap-2 rounded-xl px-2 py-1 hover:bg-slate-50">
                            <input
                              type="checkbox"
                              checked={selectedOwnersMap.all || false}
                              onChange={() => toggleOwnerSelection("all")}
                            />
                            <span>Tous les owners</span>
                          </label>
                          {filteredEmployees.map((emp) => (
                            <label
                              key={emp.id}
                              className="flex cursor-pointer items-center gap-2 rounded-xl px-2 py-1 hover:bg-slate-50"
                            >
                              <input
                                type="checkbox"
                                checked={selectedOwnersMap[emp.id] || false}
                                onChange={() => toggleOwnerSelection(emp.id)}
                              />
                              <span className="truncate">{emp.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="relative" ref={slotDropdownRef}>
                    <button
                      type="button"
                      onClick={() =>
                        setActiveFilterDropdown((prev) => (prev === "slot" ? null : "slot"))
                      }
                      className={`rounded-full border border-white/20 px-3 py-2 transition ${activeFilterDropdown === "slot" ? "bg-white text-slate-800" : "bg-white/10"
                        }`}
                    >
                      Slots • {slotFilterLabel}
                    </button>
                    {activeFilterDropdown === "slot" && (
                      <div className="absolute right-0 z-50 mt-2 w-56 rounded-2xl bg-white p-3 text-slate-700 shadow-2xl">
                        <div className="max-h-56 space-y-1 overflow-y-auto text-sm">
                          <button
                            type="button"
                            onClick={() => setSelectedSlot("all")}
                            className={`w-full rounded-xl px-2 py-1 text-left hover:bg-slate-50 ${selectedSlot === "all" ? "bg-slate-100 font-semibold" : ""
                              }`}
                          >
                            Tous les slots
                          </button>
                          {slots.map((slot) => {
                            const label = slot.name || slot.title || slot.label || `Slot ${slot.id}`;
                            const current = isSlotCurrent(slot);
                            return (
                              <button
                                type="button"
                                key={slot.id}
                                onClick={() => setSelectedSlot(String(slot.id))}
                                className={`w-full rounded-xl px-2 py-1 text-left hover:bg-slate-50 ${String(slot.id) === String(selectedSlot)
                                  ? "bg-slate-100 font-semibold"
                                  : ""
                                  }`}
                              >
                                <span className="flex items-center justify-between gap-2">
                                  <span>{label}</span>
                                  {current && (
                                    <span className="text-xs font-semibold text-emerald-600">
                                      En cours
                                    </span>
                                  )}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                    </>
                  )}
                </div>
              </div>
              {!showAddForm && (viewMode === "project" || viewMode === "myObjectives") && (
                <button
                  className="inline-flex items-center justify-center rounded-2xl bg-white/90 px-5 py-3 text-sm font-semibold text-slate-900 shadow-lg transition hover:-translate-y-0.5"
                  onClick={handleAddObjectiveClick}
                >
                  <span className="mr-2 text-lg leading-none text-indigo-600">+</span>
                  Nouvel objectif
                </button>
              )}
            </div>
          </div>
        </section>

        {showAddForm && (
          <div className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
            <AddObjectiveForm
              pendingNewObjective={pendingNewObjective}
              newObjectiveAnimating={newObjectiveAnimating}
              handleNewObjectiveChange={handleNewObjectiveChange}
              handleAiPredictions={handleAiPredictions} // MODIFIÉ
              predictedTokens={predictedTokens}
              predictingTokens={predictingTokens}
              predictedTitle={predictedTitle}
              predictingTitle={predictingTitle}
              museTitleRewrite={museTitleRewrite}
              onUndoTitleRewrite={() => {
                if (!museTitleRewrite) return;
                setPendingNewObjective((prev) => (prev ? { ...prev, name: museTitleRewrite.from } : prev));
                setMuseTitleRewrite(null);
              }}
              titleSuggestionDismissed={titleSuggestionDismissed}
              setTitleSuggestionDismissed={setTitleSuggestionDismissed}
              projects={projects}
              employees={employees}
              activeEmployees={activeEmployees}
              projectParticipants={
                pendingNewObjective?.project_id
                  ? projectParticipants[pendingNewObjective.project_id] || []
                  : []
              }
              iterationsById={iterationsById}
              sprintOptions={sprintOptions.filter(
                (sp) => String(sp.project_id) === String(pendingNewObjective?.project_id)
              )} // Filter sprints for selected project
              handleCancelNewObjective={handleCancelNewObjective}
              handleSaveNewObjective={handleSaveNewObjective}
              handleAddSubTask={handleAddSubTask}
              handleSubTaskChange={handleSubTaskChange}
              handleRemoveSubTask={handleRemoveSubTask}
            />
          </div>
        )}

        {commentObjective && (
          <CommentPopup
            commentText={commentText}
            handleCommentTextChange={handleCommentTextChange}
            handleCommentSave={handleCommentSave}
            closeCommentPopup={closeCommentPopup}
          />
        )}

        <section className="space-y-4">
          {objectivesLoading && objectives.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-6 text-center text-sm text-slate-600">
              Chargement des objectifs...
            </div>
          ) : (
            <>
              {objectivesLoading && objectives.length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-white/80 px-3 py-2 text-xs text-slate-500">
                  Actualisation en cours...
                </div>
              )}
              <div className="flex flex-col gap-4 overflow-x-auto pb-4 lg:flex-row" id="objective-board-kanban">
                {displayedColumns.map((column) => (
                  <KanbanColumn
                    key={column.status}
                    title={column.title}
                    status={column.status}
                    objectives={column.objectives}
                    onUpdateObjective={handleUpdateObjectiveInList}
                    viewMode={viewMode}
                    getProjectName={getProjectName}
                    getProjectPriorityInfo={getProjectPriorityInfoForId}
                    getOwnerName={getOwnerName}
                    getSprintName={getSprintName}
                    getOwnerInitials={getOwnerInitials}
                    handleDragStart={handleDragStart}
                    handleDragOver={handleDragOver}
                    handleDrop={handleDrop}
                    enableEditing={enableEditing}
                    handleEditingChange={handleEditingChange}
                    handleEditingBlur={handleEditingBlur}
                    handleSelectChange={handleSelectChange}
                    editing={editing}
                    sprintOptions={sprintOptions}
                    handleDeleteObjective={handleDeleteObjective}
                    openCommentPopup={openCommentPopup}
                    navigate={navigate}
                    allSprints={allSprints}
                    employees={employees}
                    onRequestSuggestion={handleRequestSuggestion}
                    onToggleSubscription={handleToggleObjectiveSubscription}
                    currentUserId={user?.id ?? null}
                    totalObjectives={column.total}
                    hasMore={column.hasMore}
                    onLoadMore={() => handleLoadMoreColumn(column.status)}
                    isSearchActive={isSearchActive}
                  />
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default ObjectiveBoard;
